export type DatadogMonitorStatus =
  | 'OK'
  | 'Warn'
  | 'Alert'
  | 'Data unavailable'
  | 'Not checked';

export interface MonitorMetricConfig {
  displayName?: string;
  query?: string;
  type?: string;
  monitorUrl?: string;
  monitorURL?: string;
  monitorId?: number;
}

export interface MonitorCheckEntry {
  checkedAt: string;
  metrics?: Record<string, number>;
  error?: string;
}

export interface DatadogMonitor {
  key: string;
  name: string;
  config: MonitorMetricConfig;
  index: number;
}

export interface DatadogMonitorStatusItem extends DatadogMonitor {
  status: DatadogMonitorStatus;
}

const NUMERIC_MONITOR_STATUS: Record<number, DatadogMonitorStatus> = {
  0: 'OK',
  1: 'Warn',
  2: 'Alert',
};

export const isDatadogMonitorConfig = (config?: MonitorMetricConfig): boolean =>
  config?.type?.toLowerCase() === 'monitor' ||
  !!config?.monitorUrl ||
  !!config?.monitorURL ||
  config?.monitorId != null;

const getDatadogMonitorConfigs = (
  metricsConfig?: MonitorMetricConfig[],
): MonitorMetricConfig[] =>
  (metricsConfig ?? []).filter(isDatadogMonitorConfig);

const getMonitorIdFromUrl = (config: MonitorMetricConfig): string | undefined =>
  (config.monitorUrl ?? config.monitorURL)?.match(/\/monitors\/(\d+)/)?.[1];

export const getDatadogMonitorName = (
  config: MonitorMetricConfig,
  index: number,
): string =>
  config.displayName?.trim() ||
  config.query?.trim() ||
  `Datadog monitor ${
    config.monitorId ?? getMonitorIdFromUrl(config) ?? index + 1
  }`;

export const getDatadogMonitors = (
  metricsConfig?: MonitorMetricConfig[],
): DatadogMonitor[] =>
  getDatadogMonitorConfigs(metricsConfig).map((config, index) => ({
    key: `datadog-monitor-${
      config.monitorId ?? getMonitorIdFromUrl(config) ?? index + 1
    }-${index}`,
    name: getDatadogMonitorName(config, index),
    config,
    index,
  }));

export const getDatadogMonitorNames = (
  metricsConfig?: MonitorMetricConfig[],
): string[] => getDatadogMonitors(metricsConfig).map(({ name }) => name);

export const isDatadogMonitorName = (
  name: string,
  metricsConfig?: MonitorMetricConfig[],
): boolean =>
  getDatadogMonitorConfigs(metricsConfig).some(
    (config, index) =>
      getDatadogMonitorName(config, index) === name ||
      config.displayName === name ||
      config.query === name ||
      String(config.monitorId ?? '') === name ||
      config.monitorUrl === name ||
      config.monitorURL === name,
  );

export const normalizeDatadogMonitorStatus = (
  value: unknown,
  options?: { notChecked?: boolean; unavailable?: boolean },
): DatadogMonitorStatus => {
  if (options?.notChecked) return 'Not checked';
  if (options?.unavailable || value == null || value === '') {
    return 'Data unavailable';
  }

  if (typeof value === 'number') {
    return NUMERIC_MONITOR_STATUS[value] ?? 'Data unavailable';
  }

  const normalized = String(value).trim().toLowerCase().replace(/[_-]/g, ' ');
  if (normalized === '0' || normalized === 'ok') return 'OK';
  if (normalized === '1' || normalized === 'warn' || normalized === 'warning') {
    return 'Warn';
  }
  if (normalized === '2' || normalized === 'alert') return 'Alert';
  if (
    normalized === 'no data' ||
    normalized === 'unknown' ||
    normalized === 'data unavailable'
  ) {
    return 'Data unavailable';
  }

  return 'Data unavailable';
};

export const getDatadogMonitorStatusColor = (
  status: DatadogMonitorStatus,
): string => {
  switch (status) {
    case 'OK':
      return 'green';
    case 'Warn':
      return 'orange';
    case 'Alert':
      return 'red';
    case 'Data unavailable':
      return 'default';
    case 'Not checked':
    default:
      return 'default';
  }
};

export const getMonitorValue = (
  values: Record<string, number> | undefined,
  monitor: DatadogMonitor,
): number | undefined => {
  const { config, name } = monitor;

  const candidateKeys = [
    config.monitorId != null ? String(config.monitorId) : undefined,
    config.monitorUrl,
    config.monitorURL,
    name,
    config.displayName,
    config.query,
  ].filter((key): key is string => !!key);

  return candidateKeys.reduce<number | undefined>(
    (value, key) => value ?? values?.[key],
    undefined,
  );
};

export const getLatestMonitorStatuses = (
  entries: MonitorCheckEntry[] | undefined,
  metricsConfig?: MonitorMetricConfig[],
  phaseStatus?: string,
): DatadogMonitorStatusItem[] => {
  const monitors = getDatadogMonitors(metricsConfig);
  if (phaseStatus === 'yet_to_start') {
    return monitors.map((monitor) => ({
      ...monitor,
      status: 'Not checked',
    }));
  }

  const latestEntry = entries?.[entries.length - 1];
  if (!latestEntry) {
    return monitors.map((monitor) => ({
      ...monitor,
      status: 'Not checked',
    }));
  }

  return monitors.map((monitor) => ({
    ...monitor,
    status: normalizeDatadogMonitorStatus(
      getMonitorValue(latestEntry?.metrics, monitor),
      { unavailable: !!latestEntry?.error },
    ),
  }));
};

export const getAggregateMonitorStatus = (
  statuses: DatadogMonitorStatus[],
): DatadogMonitorStatus => {
  if (statuses.includes('Alert')) return 'Alert';
  if (statuses.includes('Warn')) return 'Warn';
  if (statuses.includes('Data unavailable')) return 'Data unavailable';
  if (statuses.includes('OK')) return 'OK';
  return 'Not checked';
};

export const withoutDatadogMonitors = (
  values: Record<string, number> | null | undefined,
  metricsConfig?: MonitorMetricConfig[],
): Record<string, number> | null => {
  if (!values) return null;
  const entries = Object.entries(values).filter(
    ([name]) => !isDatadogMonitorName(name, metricsConfig),
  );
  return entries.length > 0 ? Object.fromEntries(entries) : null;
};
